import React, { useState, useEffect } from "react";
import { Container } from 'react-bootstrap';
import { PROD } from "../../../common/constants";
import { Box, Button, Card, Stack, Typography, Dialog, DialogContent, DialogActions, DialogTitle, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { QuickSearchToolbar } from "../../../payment/common/commonFunctions";
import { GET_CONSIGNMENT_CANCELLATION_REQUEST_LIST, APPROVE_CONSIGNMENT_CANCELLATION_REQUEST } from "../../../common/URLs";
import { loaderService } from "../../../common/custom-loader/loader-service";
import { getLocalStorageItem } from "../../../common/localStorageUtils";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import apiClient from "../../../axios";
import { ApproveIcon, CancelIcon } from "../../../common/icons";
import moment from "moment";

function ConsignmentCancellationApprovalList() {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState('Pending');
    const [listData, setListData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isApiCalled, setIsApiCalled] = useState(false);

    // Dialog States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionType, setActionType] = useState(''); // 'Approve' or 'Reject'
    const [comment, setComment] = useState('');
    const [commentError, setCommentError] = useState(false);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setSelectedRows([]); // Clear selection when switching tabs
    };

    const columns = [
        { field: 'consignmentNo', headerName: t('pit.consignmentNo') || 'Consignment No', width: 170 },
        { field: 'consignmentStatus', headerName: 'Consignment Status', width: 170 },
        { field: 'consignmentOwnerType', headerName: 'Owner Type', width: 170 },
        { field: 'consignmentOwnerUrn', headerName: 'Owner URN', width: 170 },
        { field: 'consignmentOwnerPoNo', headerName: 'Owner Po', width: 170 },
        { field: 'consignmentSourcePoNo', headerName: 'Source Po', width: 170 },
        { field: 'consignmentSourceType', headerName: 'Source Type', width: 170 },
        { field: 'consignmentSourceUrn', headerName: 'Source URN', width: 170 },
        { field: 'consignmentDestinationType', headerName: 'Destination Type', width: 170 },
        { field: 'consignmentDestinationUrn', headerName: 'Destination URN', width: 170 },
        { field: 'vehicleNo', headerName: 'Vehicle Number', width: 170 },
        { field: 'consignmentWeight', headerName: 'Consignment Quantity (MT)', width: 170 },
        // {
        //     field: 'createdOn', headerName: 'Request Date', width: 150,
        //     valueFormatter: params => params.value !== null ? moment(params?.value).format("DD/MM/YYYY") : ''
        // },
        { field: 'comment', headerName: 'Reason', width: 250 },
    ];

    useEffect(() => {
        getRequestList();
    }, [tabValue]);

    const getRequestList = () => {
        loaderService.showLoader();

        let payload = {};
        if (tabValue === 'Pending') {
            payload = { isRequestApproved: false };
        } else if (tabValue === 'Approved') {
            payload = { isRequestApproved: true };
        } else if (tabValue === 'Rejected') {
            payload = { isRequestApproved: false, isRequestRejected: true };
        }

        apiClient
            .post(
                GET_CONSIGNMENT_CANCELLATION_REQUEST_LIST,
                payload,
                {
                    headers: {
                        Authorization: "Bearer " + getLocalStorageItem('id_token')
                    },
                },
                null
            )
            .then((response) => {
                setListData(response.data?.consignmentCancellationRequestList || []);
                loaderService.hideLoader();
            })
            .catch(function (error) {
                console.log(error);
                loaderService.hideLoader();
                toast.error(error.message);
            });
    };

    const handleOpenDialog = (type) => {
        setActionType(type);
        setComment('');
        setCommentError(false);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setComment('');
        setCommentError(false);
    };

    const handleConfirmAction = async () => {
        if (!comment || comment.trim() === "") {
            setCommentError(true);
            return;
        }

        if (isApiCalled) return;
        setIsApiCalled(true);
        loaderService.showLoader();

        const payload = {
            consignmentNoList: selectedRows,
            isRequestApproved: actionType === 'Approve',
            comment: comment.trim()
        };

        apiClient
            .post(
                APPROVE_CONSIGNMENT_CANCELLATION_REQUEST,
                payload,
                {
                    headers: {
                        Authorization: "Bearer " + getLocalStorageItem('id_token'),
                    },
                },
                null
            )
            .then((response) => {
                if (response.data?.isSuccess === true || response.status === 200) {
                    toast.success(response?.data?.message || `Requests ${actionType}d successfully!`);
                    getRequestList();
                    setSelectedRows([]);
                    handleCloseDialog();
                } else {
                    toast.error(response?.data?.message || "Error processing request");
                }
            })
            .catch(function (error) {
                console.log(error);
                toast.error(error.message);
            })
            .finally(() => {
                loaderService.hideLoader();
                setIsApiCalled(false);
            });
    };

    const renderDataGrid = () => (
        <Box sx={{ height: 450, width: "100%" }}>
            <DataGrid
                rows={listData}
                columns={columns}
                getRowId={(row) => row.consignmentNo}
                getRowHeight={() => 'auto'}
                pageSize={10}
                rowsPerPageOptions={[5]}
                components={{ Toolbar: QuickSearchToolbar }}
                checkboxSelection={tabValue === 'Pending'}
                onSelectionModelChange={(newSelection) => {
                    setSelectedRows(newSelection);
                }}
                selectionModel={selectedRows}
                disableSelectionOnClick
            />
        </Box>
    );

    return (
        <>
            <Container fluid>
                {!PROD && <p style={{ textAlign: "center", fontSize: "2vh" }}>PIT49</p>}

                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h5" gutterBottom>
                        Cancellation Requests
                    </Typography>
                    {tabValue === 'Pending' && (
                        <Box sx={{ m: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={selectedRows.length === 0}
                                onClick={() => handleOpenDialog('Approve')}
                                startIcon={<ApproveIcon />}
                            >
                                Approve
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={selectedRows.length === 0}
                                onClick={() => handleOpenDialog('Reject')}
                                startIcon={<CancelIcon />}
                            >
                                Reject
                            </Button>
                        </Box>
                    )}
                </Stack>

                <Card>
                    <TabContext value={tabValue}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={handleTabChange}>
                                <Tab label="Pending" value="Pending" />
                                <Tab label="Approved" value="Approved" />
                                <Tab label="Rejected" value="Rejected" />
                            </TabList>
                        </Box>

                        <TabPanel value="Pending">{renderDataGrid()}</TabPanel>
                        <TabPanel value="Approved">{renderDataGrid()}</TabPanel>
                        <TabPanel value="Rejected">{renderDataGrid()}</TabPanel>
                    </TabContext>
                </Card>
            </Container>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm {actionType}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to <strong>{actionType.toLowerCase()}</strong> request(s)?
                    </Typography>

                    <TextField
                        autoFocus
                        margin="dense"
                        id="action-comment"
                        label="Comment"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            if (e.target.value.trim()) setCommentError(false);
                        }}
                        required
                        error={commentError}
                        helperText={commentError ? "A comment is required." : ""}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isApiCalled}>
                        Close
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={actionType === 'Approve' ? 'primary' : 'error'}
                        variant="contained"
                        disabled={isApiCalled || !comment.trim()}
                    >
                        Confirm {actionType}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ConsignmentCancellationApprovalList;
